"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Simulator = exports.LatencyType = void 0;
const utils_1 = require("@streamr/utils");
const pings_1 = require("./pings");
const heap_1 = __importDefault(require("heap"));
const debugHelpers_1 = require("../../helpers/debugHelpers");
const identifiers_1 = require("../../identifiers");
const logger = new utils_1.Logger(module);
var LatencyType;
(function (LatencyType) {
    LatencyType["NONE"] = "NONE";
    LatencyType["RANDOM"] = "RANDOM";
    LatencyType["REAL"] = "REAL";
    LatencyType["FIXED"] = "FIXED";
})(LatencyType || (exports.LatencyType = LatencyType = {}));
// One-way 'pipe' of messages
class Association {
    connectedCallback;
    sourceConnection;
    destinationConnection;
    lastOperationAt = 0;
    closing = false;
    constructor(sourceConnection, destinationConnection, connectedCallback) {
        this.connectedCallback = connectedCallback;
        this.sourceConnection = sourceConnection;
        this.destinationConnection = destinationConnection;
    }
    setDestinationConnection(connection) {
        this.destinationConnection = connection;
    }
    getLastOperationAt() {
        return this.lastOperationAt;
    }
    setLastOperationAt(executionTime) {
        this.lastOperationAt = executionTime;
    }
    setClosing() {
        this.closing = true;
    }
    isClosing() {
        return this.closing;
    }
}
class SimulatorOperation {
    executionTime;
    association;
    static objectCounter = 0;
    objectId = 0;
    constructor(executionTime, association) {
        this.executionTime = executionTime;
        this.association = association;
        this.objectId = SimulatorOperation.objectCounter;
        SimulatorOperation.objectCounter++;
    }
}
class ConnectOperation extends SimulatorOperation {
    sourceConnection;
    targetDescriptor;
    constructor(executionTime, association, sourceConnection, targetDescriptor) {
        super(executionTime, association);
        this.sourceConnection = sourceConnection;
        this.targetDescriptor = targetDescriptor;
    }
}
class SendOperation extends SimulatorOperation {
    data;
    constructor(executionTime, association, data) {
        super(executionTime, association);
        this.data = data;
    }
}
class CloseOperation extends SimulatorOperation {
}
class Simulator {
    stopped = false;
    connectors = new Map();
    latencyTable;
    associations = new Map();
    latencyType;
    fixedLatency;
    loopCounter = 0;
    MAX_LOOPS = 1000;
    operationQueue = new heap_1.default((a, b) => {
        if ((a.executionTime - b.executionTime) === 0) {
            return (a.objectId - b.objectId);
        }
        else {
            return (a.executionTime - b.executionTime);
        }
    });
    simulatorTimeout;
    constructor(latencyType = LatencyType.NONE, fixedLatency) {
        this.latencyType = latencyType;
        this.fixedLatency = fixedLatency;
        if (this.latencyType === LatencyType.REAL) {
            this.latencyTable = (0, pings_1.getRegionDelayMatrix)();
        }
        if ((this.latencyType === LatencyType.FIXED) && (this.fixedLatency === undefined)) {
            throw new Error('LatencyType.FIXED requires the desired latency to be given as second parameter');
        }
        this.generateExecutionTime = this.generateExecutionTime.bind(this);
        this.getLatency = this.getLatency.bind(this);
        this.executeCloseOperation = this.executeCloseOperation.bind(this);
        this.executeConnectOperation = this.executeConnectOperation.bind(this);
        this.executeSendOperation = this.executeSendOperation.bind(this);
        this.executeQueuedOperations = this.executeQueuedOperations.bind(this);
        this.accept = this.accept.bind(this);
        this.send = this.send.bind(this);
        this.close = this.close.bind(this);
        this.scheduleNextTimeout = this.scheduleNextTimeout.bind(this);
        this.scheduleOperation = this.scheduleOperation.bind(this);
    }
    generateExecutionTime(association, sourceRegion, targetRegion) {
        let executionTime = Date.now() + this.getLatency(sourceRegion, targetRegion);
        if (association.getLastOperationAt() > executionTime) {
            executionTime = association.getLastOperationAt();
        }
        return executionTime;
    }
    getLatency(sourceRegion, targetRegion) {
        let latency = 0;
        if (this.latencyType === LatencyType.FIXED) {
            latency = this.fixedLatency;
        }
        if (this.latencyType === LatencyType.REAL) {
            if (sourceRegion === undefined || targetRegion === undefined || sourceRegion > 15 || targetRegion > 15) {
                logger.error('invalid region index given to Simulator');
                throw new Error('invalid region index given to Simulator');
            }
            latency = this.latencyTable[sourceRegion][targetRegion];
        }
        if (this.latencyType === LatencyType.RANDOM) {
            latency = Math.random() * (250 - 5) + 5;
        }
        return latency;
    }
    accept(sourceConnection, targetConnection) {
        const sourceAssociation = this.associations.get(sourceConnection.connectionId);
        if (!sourceAssociation) {
            logger.error('source association not found in accept()');
            return;
        }
        sourceAssociation.setDestinationConnection(targetConnection);
        const targetAssociation = new Association(targetConnection, sourceConnection);
        this.associations.set(targetConnection.connectionId, targetAssociation);
        sourceAssociation.connectedCallback();
    }
    addConnector(connector) {
        this.connectors.set((0, identifiers_1.toNodeId)(connector.getPeerDescriptor()), connector);
    }
    executeConnectOperation(operation) {
        const target = this.connectors.get((0, identifiers_1.toNodeId)(operation.targetDescriptor));
        if (!target) {
            logger.error('Target connector not found when executing connect operation');
            operation.association.connectedCallback('Target connector not found');
            return;
        }
        target.handleIncomingConnection(operation.sourceConnection);
    }
    executeCloseOperation(operation) {
        if (this.stopped) {
            return;
        }
        const target = operation.association.destinationConnection;
        let counterAssociation;
        if (target) {
            counterAssociation = this.associations.get(target.connectionId);
        }
        if (!target || !counterAssociation) {
            this.associations.delete(operation.association.sourceConnection.connectionId);
        }
        else if (!counterAssociation.isClosing()) {
            target.handleIncomingDisconnection();
            this.close(target);
        }
        else {
            // this is the 'ack' of the CloseOperation to the original closer
            this.associations.delete(target.connectionId);
            this.associations.delete(operation.association.sourceConnection.connectionId);
        }
    }
    executeSendOperation(operation) {
        if (this.stopped) {
            return;
        }
        const target = operation.association.destinationConnection;
        target.handleIncomingData(operation.data);
    }
    executeQueuedOperations() {
        const currentTime = Date.now();
        while (this.operationQueue.size() > 0
            && this.operationQueue.peek().executionTime <= currentTime) {
            const operation = this.operationQueue.pop();
            if (operation instanceof ConnectOperation) {
                this.executeConnectOperation(operation);
            }
            else if (operation instanceof CloseOperation) {
                this.executeCloseOperation(operation);
            }
            else if (operation instanceof SendOperation) {
                this.executeSendOperation(operation);
            }
            else {
                logger.error('Unknown SimulatorOperation');
            }
            this.loopCounter++;
            if (this.loopCounter >= this.MAX_LOOPS) {
                this.loopCounter = 0;
                setTimeout(() => this.executeQueuedOperations(), 0);
                return;
            }
        }
        this.scheduleNextTimeout();
    }
    scheduleNextTimeout() {
        if (this.simulatorTimeout) {
            clearTimeout(this.simulatorTimeout);
            this.simulatorTimeout = undefined;
        }
        const currentTime = Date.now();
        const firstOperation = this.operationQueue.peek();
        if (!firstOperation) {
            return;
        }
        const firstOperationTime = firstOperation.executionTime;
        const timeDifference = firstOperationTime - currentTime;
        this.simulatorTimeout = setTimeout(this.executeQueuedOperations, timeDifference);
    }
    scheduleOperation(operation) {
        this.operationQueue.push(operation);
        this.scheduleNextTimeout();
    }
    connect(sourceConnection, targetDescriptor, connectedCallback) {
        if (this.stopped) {
            logger.error('connect() called on a stopped simulator ' + (new Error().stack));
            return;
        }
        debugHelpers_1.debugVars.simulatorHeapSize = this.operationQueue.size();
        const association = new Association(sourceConnection, undefined, connectedCallback);
        this.associations.set(sourceConnection.connectionId, association);
        const executionTime = this.generateExecutionTime(association, sourceConnection.localPeerDescriptor.region, targetDescriptor.region);
        association.setLastOperationAt(executionTime);
        const operation = new ConnectOperation(executionTime, association, sourceConnection, targetDescriptor);
        this.scheduleOperation(operation);
    }
    close(sourceConnection) {
        if (this.stopped) {
            return;
        }
        const association = this.associations.get(sourceConnection.connectionId);
        if (!association) {
            return;
        }
        association.setClosing();
        const executionTime = this.generateExecutionTime(association, sourceConnection.localPeerDescriptor.region, sourceConnection.getPeerDescriptor()?.region);
        association.setLastOperationAt(executionTime);
        const operation = new CloseOperation(executionTime, association);
        this.scheduleOperation(operation);
    }
    send(sourceConnection, data) {
        if (this.stopped) {
            return;
        }
        const association = this.associations.get(sourceConnection.connectionId);
        if (!association) {
            return;
        }
        if (association.isClosing()) {
            logger.trace('Tried to call send() on a closing association');
            return;
        }
        const executionTime = this.generateExecutionTime(association, sourceConnection.localPeerDescriptor.region, association.destinationConnection.localPeerDescriptor.region);
        association.setLastOperationAt(executionTime);
        const operation = new SendOperation(executionTime, association, data);
        this.scheduleOperation(operation);
    }
    stop() {
        this.stopped = true;
        logger.info(this.associations.size + ' associations in the beginning of stop()');
        if (this.simulatorTimeout) {
            clearTimeout(this.simulatorTimeout);
        }
    }
}
exports.Simulator = Simulator;
//# sourceMappingURL=Simulator.js.map